import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import AnimatedNumber from '../shared/AnimatedNumber';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { GRADIENTS, SHADOWS_LIGHT, SHADOWS_DARK } from '../../theme/tokens';

const MotionCircle = motion.circle;

const KM_OPTIONS = [2, 5, 10, 15];

// Ring index mapping: 2km→0, 5km→1, 10km→2, 15km→2
const kmToRingIndex = (km) => {
  if (km <= 2) return 0;
  if (km <= 5) return 1;
  return 2;
};

// Radar SVG with animated sweep, pulse, and active ring highlight
function RadarSVG({ value }) {
  const theme = useTheme();
  const reduced = useReducedMotionPreferred();
  const activeRingIdx = kmToRingIndex(value);
  const showHalo = value === 15;

  const ringRadii = [16, 32, 48];
  const ringOpacities = [0.9, 0.55, 0.25];
  const primaryColor = theme.palette.primary.main;

  return (
    <Box
      component="svg"
      viewBox="0 0 104 104"
      width={104}
      height={104}
      role="img"
      aria-label={`${value} kilometer search radius`}
      sx={{ flexShrink: 0, display: 'block' }}
    >
      <defs>
        {/* Drop-shadow filter for center dot */}
        <filter id="radar-center-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
          <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor={primaryColor} floodOpacity="0.6" />
        </filter>
        {/* CSS keyframe sweep — disabled via media query for reduced motion */}
        <style>{`
          @keyframes radar-sweep {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          .radar-sweep-line {
            transform-origin: 52px 52px;
            animation: radar-sweep 4s linear infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .radar-sweep-line { animation: none; }
          }
        `}</style>
      </defs>

      {/* Concentric rings */}
      {ringRadii.map((r, idx) => (
        <MotionCircle
          key={r}
          cx={52}
          cy={52}
          r={r}
          fill="none"
          stroke={primaryColor}
          strokeOpacity={idx === activeRingIdx ? 1 : ringOpacities[idx]}
          strokeWidth={idx === activeRingIdx ? 2 : 1}
          animate={
            !reduced && idx === activeRingIdx
              ? { scale: [1, 1.08, 1] }
              : {}
          }
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{ transformOrigin: '52px 52px' }}
        />
      ))}

      {/* Halo for 15km — extra ring at radius 52 */}
      {showHalo && (
        <MotionCircle
          cx={52}
          cy={52}
          r={52}
          fill="none"
          stroke={primaryColor}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.15 }}
          transition={{ duration: 0.4 }}
          strokeWidth={1}
        />
      )}

      {/* Sweep line — CSS animated so @media prefers-reduced-motion can turn it off */}
      {!reduced && (
        <line
          className="radar-sweep-line"
          x1={52}
          y1={52}
          x2={52}
          y2={4}
          stroke={primaryColor}
          strokeOpacity={0.4}
          strokeWidth={1.5}
        />
      )}

      {/* Center pulse */}
      {!reduced && (
        <MotionCircle
          cx={52}
          cy={52}
          r={4}
          fill={primaryColor}
          animate={{ scale: [1, 1.3, 1], opacity: [0.9, 0.4, 0.9] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: '52px 52px' }}
          filter="url(#radar-center-glow)"
        />
      )}

      {/* Static center dot (always present, for reduced-motion) */}
      {reduced && (
        <circle
          cx={52}
          cy={52}
          r={4}
          fill={primaryColor}
          filter="url(#radar-center-glow)"
        />
      )}
    </Box>
  );
}

RadarSVG.propTypes = {
  value: PropTypes.number.isRequired,
};

// Individual radius chip button with shared layoutId background
function RadiusChip({ km, isActive, onClick, reduced }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const glowShadow = isLight ? SHADOWS_LIGHT.glowPrimary : SHADOWS_DARK.glowPrimary;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(km);
    }
  };

  return (
    <Box
      component="button"
      role="radio"
      aria-checked={isActive}
      tabIndex={0}
      onClick={() => onClick(km)}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '999px',
        px: 2,
        py: 0.75,
        cursor: 'pointer',
        background: 'transparent',
        color: isActive ? 'white' : 'text.secondary',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1,
        outline: 'none',
        transition: 'color 0.2s',
        overflow: 'hidden',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* Sliding gradient background for active state */}
      {isActive && (
        <motion.div
          layoutId="radius-active"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '999px',
            background: GRADIENTS.brand,
            boxShadow: glowShadow,
            zIndex: 0,
          }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
        />
      )}
      <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
        {km} KM
      </Box>
    </Box>
  );
}

RadiusChip.propTypes = {
  km: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  reduced: PropTypes.bool.isRequired,
};

/**
 * SearchRadius
 * Two-column layout: animated radar SVG on the left, radius chip selector on the right.
 * Stacks to single column on xs.
 *
 * @param {number} value      — currently selected radius in KM
 * @param {(km: number) => void} onChange
 */
function SearchRadius({ value, onChange }) {
  const reduced = useReducedMotionPreferred();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'center', md: 'center' },
        gap: { xs: 2, md: 3 },
      }}
    >
      {/* Radar SVG */}
      <RadarSVG value={value} />

      {/* Chips + caption */}
      <Box sx={{ flex: 1, width: '100%' }}>
        <Box
          role="radiogroup"
          aria-label="Search radius"
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, justifyContent: { xs: 'center', md: 'flex-start' } }}
        >
          {KM_OPTIONS.map((km) => (
            <RadiusChip
              key={km}
              km={km}
              isActive={value === km}
              onClick={onChange}
              reduced={reduced}
            />
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Showing libraries within{' '}
          <AnimatedNumber value={value} duration={0.4} />{' '}
          KM of your location.
        </Typography>
      </Box>
    </Box>
  );
}

SearchRadius.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SearchRadius;
